import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader } from "../components/Primitives";
import { fmtMoney } from "../lib/money";
import { downloadCsv } from "../lib/csv";
import { parseUtc } from "../lib/datetime";
import {
    Music2, Search, ChevronUp, ChevronDown, Download, Loader2,
    Columns3, Check, Play, RefreshCw, FileDown,
} from "lucide-react";

const BREAKDOWNS = [
    { k: "campaign", label: "Campaign · daily" },
    { k: "adgroup", label: "Ad group · daily" },
    { k: "ad", label: "Ad · daily" },
    { k: "audience_country", label: "Country" },
    { k: "audience_gender", label: "Gender" },
    { k: "audience_age", label: "Age" },
];

// Metric picker — matches TikTokInsight columns. Order = display order.
const ALL_METRICS = [
    { key: "impressions", label: "Impressions", format: "int" },
    { key: "clicks", label: "Clicks", format: "int" },
    { key: "spend", label: "Spend", format: "money" },
    { key: "client_budget", label: "Client Budget", format: "money" },
    { key: "daily_budget_live", label: "Daily Budget (live)", format: "money" },
    { key: "lifetime_budget_live", label: "Lifetime Budget (live)", format: "money" },
    { key: "ctr", label: "CTR %", format: "float" },
    { key: "cpm", label: "CPM", format: "money" },
    { key: "reach", label: "Reach", format: "int" },
    { key: "frequency", label: "Frequency", format: "float" },
    { key: "conversion", label: "Conversions", format: "int" },
    { key: "app_install", label: "App activations", format: "int" },
    { key: "video_watched_2s", label: "2-sec video views", format: "int" },
    { key: "video_watched_6s", label: "6-sec video views", format: "int" },
    { key: "video_views_p25", label: "Video Views (25%)", format: "int" },
    { key: "video_views_p50", label: "Video Views (50%)", format: "int" },
    { key: "video_views_p75", label: "Video Views (75%)", format: "int" },
    { key: "video_views_p100", label: "Video Views (100%)", format: "int" },
    { key: "likes", label: "Likes", format: "int" },
    { key: "comments", label: "Comments", format: "int" },
    { key: "shares", label: "Shares", format: "int" },
];
const DEFAULT_METRICS = [
    "impressions", "clicks", "spend", "client_budget",
    "daily_budget_live", "lifetime_budget_live", "ctr", "cpm",
    "reach", "conversion", "video_views_p100",
];
const LS_KEY = "tiktok_detailed_metrics_v1";

const monthAgo = (d = new Date()) => new Date(d.setDate(d.getDate() - 29)).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

const fmtCell = (col, val, row) => {
    if (val === null || val === undefined || val === "") return "—";
    if (col.format === "money") return fmtMoney(val, row.currency || "USD");
    if (col.format === "float") return (typeof val === "number" ? val.toFixed(2) : val);
    if (col.format === "int") return Number(val).toLocaleString("en-US");
    return val;
};

const humanAgo = (iso) => {
    if (!iso) return "";
    const t = parseUtc(iso).getTime();
    const mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} d ago`;
};

export default function TikTokDetailedReports() {
    const [accounts, setAccounts] = useState([]);
    const [adAccountId, setAdAccountId] = useState("");
    const [connections, setConnections] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaignExtIds, setSelectedCampaignExtIds] = useState([]);
    const [dateFrom, setDateFrom] = useState(monthAgo());
    const [dateTo, setDateTo] = useState(today());
    const [breakdown, setBreakdown] = useState("campaign");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [sortBy, setSortBy] = useState("metric_date");
    const [sortDir, setSortDir] = useState("desc");
    const [rows, setRows] = useState([]);
    const [dimensionColumns, setDimensionColumns] = useState([]);
    const [total, setTotal] = useState(0);
    const [generating, setGenerating] = useState(false);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Metric picker
    const [metrics, setMetrics] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(LS_KEY) || "null");
            if (Array.isArray(stored) && stored.length) return stored;
        } catch (e) { void e; }
        return DEFAULT_METRICS;
    });
    const [metricMenuOpen, setMetricMenuOpen] = useState(false);
    useEffect(() => {
        try { localStorage.setItem(LS_KEY, JSON.stringify(metrics)); } catch (e) { void e; }
    }, [metrics]);
    const toggleMetric = (key) => {
        setMetrics((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    };
    const selectAllMetrics = () => setMetrics(ALL_METRICS.map((m) => m.key));
    const clearAllMetrics = () => setMetrics([]);
    const visibleMetrics = ALL_METRICS.filter((m) => metrics.includes(m.key));

    // Live budgets
    const [liveBudgets, setLiveBudgets] = useState({});

    // Only TikTok ad accounts + connections
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/ad-accounts");
                const tk = data.filter((a) => a.platform === "tiktok");
                setAccounts(tk);
                if (tk.length && !adAccountId) setAdAccountId(tk[0].id);
            } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to load ad accounts");
            }
        })();
        api.get("/platform-connections")
            .then((r) => setConnections(r.data.filter((c) => c.platform === "tiktok")))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedAcc = accounts.find((a) => a.id === adAccountId);
    const selectedConn = selectedAcc ? connections.find((c) => c.id === selectedAcc.platform_connection_id) : null;

    // Load campaigns + live budgets when ad account changes; also clear stale rows.
    useEffect(() => {
        setSelectedCampaignExtIds([]);
        setLiveBudgets({});
        setCampaigns([]);
        setRows([]);
        setHasGenerated(false);
        if (!adAccountId) return;
        (async () => {
            try {
                const { data } = await api.get("/campaigns", {
                    params: { ad_account_id: adAccountId, has_metrics: "false" },
                });
                setCampaigns(data || []);
            } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to load campaigns");
            }
        })();
        (async () => {
            try {
                const { data } = await api.get("/tiktok/live-budgets", { params: { ad_account_id: adAccountId } });
                setLiveBudgets(data.budgets || {});
            } catch (e) { void e; } // Silent — supplementary data.
        })();
    }, [adAccountId]);

    // Clear rows whenever any filter changes so the user is forced to click Generate again.
    useEffect(() => {
        setRows([]);
        setHasGenerated(false);
    }, [breakdown, dateFrom, dateTo, selectedCampaignExtIds]);

    const loadRows = async () => {
        if (!adAccountId) return;
        const params = {
            ad_account_id: adAccountId,
            date_from: dateFrom,
            date_to: dateTo,
            breakdown,
            search,
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_dir: sortDir,
        };
        if (selectedCampaignExtIds.length) {
            params.campaign_external_ids = selectedCampaignExtIds.join(",");
        }
        const { data } = await api.get("/tiktok/insights", { params });
        const enriched = (data.rows || []).map((r) => {
            const b = liveBudgets[r.external_campaign_id] || {};
            return {
                ...r,
                daily_budget_live: b.daily_budget ?? null,
                lifetime_budget_live: b.lifetime_budget ?? null,
            };
        });
        setRows(enriched);
        setDimensionColumns((data.columns || []).filter((c) => !ALL_METRICS.some((m) => m.key === c.key)));
        setTotal(data.total);
        setHasGenerated(true);
    };

    // Re-fetch on server-side pagination/sort/search (only if user has already generated once)
    useEffect(() => {
        if (!hasGenerated) return;
        loadRows().catch((err) => toast.error(err.response?.data?.detail || "Failed to load insights"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, sortBy, sortDir, search]);

    const generate = async () => {
        if (!adAccountId) return toast.error("Select an ad account");
        setGenerating(true);
        try {
            await loadRows();
            toast.success("Report loaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load report");
        } finally {
            setGenerating(false);
        }
    };

    const onSort = (key) => {
        if (!hasGenerated) return;
        if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else { setSortBy(key); setSortDir("desc"); }
    };

    // Sync today + yesterday only (fast, safe to run on-demand). Refreshes the current view too.
    const refreshTodayYesterday = async () => {
        if (!selectedConn) return toast.error("Select an ad account first");
        setRefreshing(true);
        try {
            const { data } = await api.post("/tiktok/sync-insights", {
                connection_id: selectedConn.id,
                start_date: yesterday(),
                end_date: today(),
            });
            toast.success(`Refreshed ${data.insight_rows_upserted?.toLocaleString?.("en-US") || 0} rows for today + yesterday`);
            // Refresh the visible table if the current filter overlaps today/yesterday.
            if (hasGenerated) await loadRows();
            // Reload the connection last_synced_at
            try {
                const { data: pcs } = await api.get("/platform-connections");
                setConnections(pcs.filter((c) => c.platform === "tiktok"));
            } catch (e) { void e; }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    const [backfilling, setBackfilling] = useState(false);
    // One-click backfill of every campaign under the selected connection from 1 Jun 2026 → today.
    // We go through the background sync job path (/platform-connections/{id}/sync?days=N) so the
    // request returns immediately and the underlying chunked TikTok fetch is not held under the
    // 60-100s ingress proxy timeout. Chunking (≤30-day slices) is handled server-side in
    // integrations/tiktok_service.get_daily_insights.
    const backfillFromJune1 = async () => {
        if (!selectedConn) return toast.error("Select an ad account first");
        const START = "2026-06-01";
        const days = Math.max(1, Math.ceil((new Date() - new Date(START)) / 86400000)) + 1;
        if (!window.confirm(`Backfill every campaign under "${selectedConn.display_name}" from ${START} → today (${days} days)?\n\nRuns as a background job — expect 1-5 min. Watch the "Last synced" badge on this page or refresh the table.`)) return;
        setBackfilling(true);
        try {
            const { data } = await api.post(`/platform-connections/${selectedConn.id}/sync?days=${days}`);
            toast.success(`Backfill queued — job ${data.job_id?.slice(0, 8) || ""} running in the background. Refresh in a few minutes.`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Backfill failed");
        } finally {
            setBackfilling(false);
        }
    };

    const exportCSV = () => {
        if (!rows.length) { toast.info("Nothing to export"); return; }
        const headers = [
            ...dimensionColumns.map((c) => ({ label: c.label, key: (r) => fmtCell(c, r[c.key], r) })),
            ...visibleMetrics.map((c) => ({ label: c.label, key: (r) => fmtCell(c, r[c.key], r) })),
        ];
        downloadCsv(`tiktok-${breakdown}-${dateFrom}-to-${dateTo}.csv`, headers, rows);
        toast.success(`Downloaded ${rows.length} rows · ${visibleMetrics.length} metrics`);
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

    const displayedColumns = useMemo(
        () => [...dimensionColumns, ...visibleMetrics],
        [dimensionColumns, visibleMetrics],
    );

    return (
        <div data-testid="tiktok-detailed-page">
            <PageHeader
                eyebrow="TikTok Ads · Detailed reports"
                title={<span className="inline-flex items-center gap-2"><Music2 className="h-6 w-6" style={{ color: "#FE2C55" }} />TikTok detailed insights</span>}
                subtitle="Select an ad account, optional campaigns, breakdown, and date range. Click Generate report to load synced data from the database."
                actions={
                    <div className="flex items-center gap-2">
                        {selectedConn?.last_synced_at && (
                            <div className="text-[11px] text-zinc-500 leading-tight text-right mr-1" data-testid="tiktok-last-sync-info" title={parseUtc(selectedConn.last_synced_at).toLocaleString()}>
                                <div>Last synced <span className="text-zinc-800 font-medium">{humanAgo(selectedConn.last_synced_at)}</span></div>
                            </div>
                        )}
                        <button
                            onClick={refreshTodayYesterday}
                            disabled={refreshing || backfilling || !selectedConn}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50"
                            data-testid="tiktok-refresh-today-yesterday-btn"
                            title="Pull today + yesterday's insights from TikTok"
                        >
                            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            {refreshing ? "Refreshing…" : "Refresh today+yesterday"}
                        </button>
                        <button
                            onClick={backfillFromJune1}
                            disabled={refreshing || backfilling || !selectedConn}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50"
                            data-testid="tiktok-backfill-jun1-btn"
                            title="Pull daily insights for every campaign under this connection from 1 Jun 2026 → today"
                        >
                            {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            {backfilling ? "Backfilling…" : "Backfill from Jun 1"}
                        </button>
                    </div>
                }
            />

            {!accounts.length && (
                <div className="bg-white border border-dashed border-zinc-200 p-8 rounded text-center text-sm text-zinc-500" data-testid="tiktok-no-accounts">
                    No TikTok ad accounts yet. Connect one from <a href="/ad-accounts" className="underline">Ad Accounts → Connect platform</a>.
                </div>
            )}

            {accounts.length > 0 && (
                <div className="bg-white border border-zinc-200 p-4 mb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                            <label className="overline">Ad account</label>
                            <select value={adAccountId} onChange={(e) => setAdAccountId(e.target.value)} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="tiktok-filter-account">
                                <option value="">— pick —</option>
                                {accounts.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                            </select>
                        </div>
                        <div>
                            <label className="overline">Campaigns (optional)</label>
                            <CampaignMultiSelect
                                options={campaigns.map((c) => ({ id: c.external_campaign_id, label: c.name }))}
                                selectedIds={selectedCampaignExtIds}
                                onChange={setSelectedCampaignExtIds}
                            />
                        </div>
                        <div>
                            <label className="overline">Breakdown</label>
                            <select value={breakdown} onChange={(e) => setBreakdown(e.target.value)} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="tiktok-filter-breakdown">
                                {BREAKDOWNS.map((b) => (<option key={b.k} value={b.k}>{b.label}</option>))}
                            </select>
                        </div>
                        <div className="lg:col-span-2">
                            <label className="overline">Date range</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="flex-1 min-w-0 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="tiktok-filter-from" />
                                <span className="text-zinc-400 text-xs">→</span>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="flex-1 min-w-0 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="tiktok-filter-to" />
                            </div>
                        </div>
                    </div>

                    {/* Action bar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-zinc-100">
                        <div className="text-xs text-zinc-500" data-testid="tiktok-generate-hint">
                            {!hasGenerated && rows.length === 0 && (
                                <span>Configure filters above, then click <span className="wl-accent-text font-medium">Generate report</span> to load synced data.</span>
                            )}
                            {hasGenerated && (
                                <span className="tabular-nums">{total.toLocaleString("en-US")} rows · page {page}/{totalPages} · {breakdown} breakdown</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setMetricMenuOpen((v) => !v)}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
                                    data-testid="tiktok-metrics-btn"
                                    title="Choose which metric columns to display"
                                >
                                    <Columns3 className="h-4 w-4" />
                                    Metrics ({metrics.length}/{ALL_METRICS.length})
                                </button>
                                {metricMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-30" onClick={() => setMetricMenuOpen(false)} />
                                        <div className="absolute right-0 mt-1 w-72 bg-white border border-zinc-200 rounded-md shadow-lg z-40" data-testid="tiktok-metrics-menu">
                                            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                                                <span className="overline">Metrics</span>
                                                <div className="flex items-center gap-2 text-[11px]">
                                                    <button type="button" onClick={selectAllMetrics} className="wl-accent-text hover:underline" data-testid="tiktok-metrics-select-all">Select all</button>
                                                    <span className="text-zinc-300">·</span>
                                                    <button type="button" onClick={clearAllMetrics} className="text-zinc-500 hover:text-zinc-900" data-testid="tiktok-metrics-clear">Clear</button>
                                                </div>
                                            </div>
                                            <div className="max-h-72 overflow-y-auto py-1">
                                                {ALL_METRICS.map((m) => {
                                                    const checked = metrics.includes(m.key);
                                                    return (
                                                        <label key={m.key} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-sm" data-testid={`tiktok-metric-toggle-${m.key}`}>
                                                            <span className={`h-4 w-4 rounded border flex items-center justify-center ${checked ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                                                {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                                            </span>
                                                            <input type="checkbox" checked={checked} onChange={() => toggleMetric(m.key)} className="sr-only" />
                                                            <span className="text-zinc-700">{m.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <div className="px-3 py-2 border-t border-zinc-200 text-[10px] text-zinc-500">
                                                Selection is saved locally and applies to both the table and CSV export.
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <button
                                onClick={exportCSV}
                                disabled={!rows.length}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-700"
                                data-testid="tiktok-export-csv"
                                title={rows.length ? "Download visible rows as CSV" : "Generate a report first"}
                            >
                                <FileDown className="h-4 w-4" />Download CSV
                            </button>
                            <button
                                onClick={generate}
                                disabled={generating || !adAccountId}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                style={{ background: "var(--wl-primary-hex)", color: "#fff" }}
                                data-testid="tiktok-generate-btn"
                            >
                                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                {generating ? "Generating…" : "Generate report"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {accounts.length > 0 && (
                <>
                    {/* Search + pagination header — only shown once a report exists */}
                    {hasGenerated && (
                        <div className="flex items-center justify-between mb-3 gap-3">
                            <div className="relative flex-1 max-w-md">
                                <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    placeholder="Search campaign / ad group…"
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                    className="w-full pl-9 pr-3 py-2 bg-white border border-zinc-200 rounded text-sm"
                                    data-testid="tiktok-search"
                                />
                            </div>
                            <div className="text-xs text-zinc-500 tabular-nums" data-testid="tiktok-total-count">
                                {total.toLocaleString("en-US")} rows · page {page}/{totalPages}
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-zinc-200 bg-zinc-50">
                                    {displayedColumns.map((c) => (
                                        <th key={c.key} onClick={() => onSort(c.key)} className={`overline text-left p-3 whitespace-nowrap select-none ${hasGenerated ? "cursor-pointer hover:text-zinc-900" : ""}`}>
                                            <span className="inline-flex items-center gap-1">
                                                {c.label}
                                                {sortBy === c.key && hasGenerated && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                                        {displayedColumns.map((c) => (
                                            <td key={c.key} className={`p-3 whitespace-nowrap ${["int", "money", "float"].includes(c.format) ? "text-right tabular-nums" : ""}`}>
                                                {fmtCell(c, r[c.key], r)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {!rows.length && (
                                    generating ? (
                                        <tr><td colSpan={Math.max(1, displayedColumns.length)} className="p-8 text-center text-zinc-500 text-sm">Loading report from database…</td></tr>
                                    ) : !hasGenerated ? (
                                        <tr><td colSpan={Math.max(1, displayedColumns.length)} className="p-16 text-center">
                                            <div className="inline-flex flex-col items-center gap-3 text-zinc-500">
                                                <Play className="h-10 w-10 wl-accent-text opacity-50" />
                                                <div className="font-display text-base text-zinc-700">Ready to generate a report</div>
                                                <div className="text-xs max-w-md leading-relaxed">
                                                    Pick your ad account, campaigns, breakdown, and date range above, then click{" "}
                                                    <span className="wl-accent-text font-medium">Generate report</span> to load synced data from the database.
                                                </div>
                                            </div>
                                        </td></tr>
                                    ) : (
                                        <tr><td colSpan={Math.max(1, displayedColumns.length)} className="p-10 text-center text-zinc-500 text-sm">No rows for the selected filters. Try clicking &quot;Refresh today+yesterday&quot; to pull the latest data.</td></tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination — only when we have data */}
                    {hasGenerated && (
                        <div className="mt-4 flex items-center justify-between text-sm">
                            <div className="text-xs text-zinc-500">
                                Page size&nbsp;
                                <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }} className="px-2 py-1 bg-zinc-100 border border-zinc-200 rounded text-xs" data-testid="tiktok-page-size">
                                    {[50, 100, 250, 500, 1000].map((n) => (<option key={n} value={n}>{n}</option>))}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded text-xs border border-zinc-200 hover:bg-zinc-100 disabled:opacity-40" data-testid="tiktok-prev-page">Prev</button>
                                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded text-xs border border-zinc-200 hover:bg-zinc-100 disabled:opacity-40" data-testid="tiktok-next-page">Next</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Compact searchable multi-select for campaigns (external_campaign_id).
// -----------------------------------------------------------------------------
function CampaignMultiSelect({ options, selectedIds, onChange }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (!open) { setQ(""); return; }
        const t = setTimeout(() => searchRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((o) => (o.label || "").toLowerCase().includes(needle));
    }, [q, options]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const summary = selectedIds.length === 0
        ? "All campaigns"
        : selectedIds.length === 1
            ? (options.find((o) => o.id === selectedIds[0])?.label || "1 selected")
            : `${selectedIds.length} selected`;

    const toggleOne = (id) => {
        const next = selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
        onChange(next);
    };
    const clearAll = (e) => { e.stopPropagation(); onChange([]); };
    const selectAllFiltered = () => onChange(Array.from(new Set([...selectedIds, ...filtered.map((f) => f.id)])));

    return (
        <div className="relative mt-1" ref={wrapRef} data-testid="tiktok-campaign-picker">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={options.length === 0}
                className="w-full flex items-center gap-2 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-left text-sm hover:border-zinc-400 disabled:opacity-50"
                data-testid="tiktok-campaign-picker-trigger"
            >
                <span className={`flex-1 truncate ${selectedIds.length === 0 ? "text-zinc-500" : "text-zinc-900"}`}>{summary}</span>
                {selectedIds.length > 0 && (
                    <span role="button" tabIndex={0} onClick={clearAll} onKeyDown={(e) => { if (e.key === "Enter") clearAll(e); }} className="text-[10px] uppercase text-zinc-400 hover:text-red-500" data-testid="tiktok-campaign-picker-clear">clear</span>
                )}
                <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded shadow-lg" data-testid="tiktok-campaign-picker-popover">
                    <div className="p-2 border-b border-zinc-100 flex items-center gap-2">
                        <Search className="h-4 w-4 text-zinc-400" />
                        <input
                            ref={searchRef}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search campaigns…"
                            className="flex-1 text-sm outline-none bg-transparent"
                            data-testid="tiktok-campaign-picker-search"
                        />
                        {q && filtered.length > 0 && (
                            <button type="button" onClick={selectAllFiltered} className="text-[10px] uppercase text-zinc-500 hover:wl-accent-text" data-testid="tiktok-campaign-picker-select-all">
                                Select all
                            </button>
                        )}
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                        {options.length === 0 && (
                            <div className="text-xs text-zinc-400 italic p-3">No campaigns for this ad account yet.</div>
                        )}
                        {options.length > 0 && filtered.length === 0 && (
                            <div className="text-xs text-zinc-400 italic p-3">No matches for &quot;{q}&quot;.</div>
                        )}
                        {filtered.map((o) => {
                            const active = selectedSet.has(o.id);
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => toggleOne(o.id)}
                                    title={o.label}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-zinc-50 ${active ? "bg-orange-50/60" : ""}`}
                                    data-testid={`tiktok-campaign-picker-option-${o.id}`}
                                >
                                    <span className={`h-4 w-4 rounded border flex items-center justify-center ${active ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                        {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                    </span>
                                    <span className="flex-1 truncate text-zinc-800">{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
